import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Link, Navigate } from 'react-router-dom';
import { auth } from './services/api';
import { BiProvider } from './BiContext';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Shell from './components/Shell';
import Rapport from './components/Rapport';
import Creances from './components/Creances';
import Tresorerie from './components/Tresorerie';
import Stock from './components/Stock';
import StockEvolution from './components/StockEvolution';
import Fournisseurs from './components/Fournisseurs';
import FournisseursAnalyse from './components/FournisseursAnalyse';
import VentesPerformance from './components/VentesPerformance';
import VentesAnalyse from './components/VentesAnalyse';
import Marche from './components/Marche';
import MarcheAnalyse from './components/MarcheAnalyse';
import ApvPerformance from './components/ApvPerformance';
import ApvProductivite from './components/ApvProductivite';
import ApvVentesAdd from './components/ApvVentesAdd';
import ReferentielVentesAdd from './components/ReferentielVentesAdd';
import ApvAnalyse from './components/ApvAnalyse';
import VentesJour from './components/VentesJour';
import ApvJour from './components/ApvJour';
import PrJour from './components/PrJour';
import PrPerformance from './components/PrPerformance';
import PrAnalyse from './components/PrAnalyse';
import PrStock from './components/PrStock';
import PrStockEvolution from './components/PrStockEvolution';
import Objectifs from './components/Objectifs';
import OrEnCours from './components/OrEnCours';
import FacturationType from './components/FacturationType';
import RhMasseSalariale from './components/RhMasseSalariale';
import Pl, { Sig } from './components/Pl';
import RhAnalyse from './components/RhAnalyse';
import ReferentielMarche from './components/ReferentielMarche';
import AdminAcces from './components/AdminAcces';
import AdminSocietes from './components/AdminSocietes';
import DoubleAuth from './components/DoubleAuth';
import MenuBloc from './components/MenuBloc';
import Connecteur from './components/Connecteur';

// Autorisation du connecteur Claude en attente : mémorisée avant la connexion
// Microsoft (qui ramène sur « / »), reprise une fois la personne connectée.
const DEMANDE_MCP = 'bi-demande-connecteur';
const lireDemande = () => { try { return sessionStorage.getItem(DEMANDE_MCP); } catch (e) { return null; } };

// Bascule mode jour / mode nuit : pose data-theme sur <html> (les couleurs
// vivent dans styles/tokens.css) et mémorise le choix dans le navigateur.
function BasculeTheme() {
  const [theme, setTheme] = React.useState(() => document.documentElement.dataset.theme || 'light');
  const basculer = () => {
    const t = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = t;
    try { localStorage.setItem('bi-theme', t); } catch (e) { /* navigation privée */ }
    setTheme(t);
  };
  const nuit = theme === 'dark';
  return (
    <button type="button" className="bi-theme" onClick={basculer}
      title={nuit ? 'Passer en mode jour' : 'Passer en mode nuit'} aria-label={nuit ? 'Passer en mode jour' : 'Passer en mode nuit'}>
      {nuit ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
      )}
    </button>
  );
}

// Gabarit commun : en-tête + sidebar persistante (l'arbre des rapports reste
// visible sur toutes les pages — 1 clic pour changer de rapport).
function Layout({ user, onLogout }) {
  return (
    <div className="bi-shell">
      <header className="bi-header">
        <Link to="/" className="bi-header-brand">
          A<span className="bi-gold">97</span>
          <span className="bi-header-app">BI</span>
        </Link>
        <div className="bi-header-user">
          <BasculeTheme />
          <span className="bi-header-nom">{user.prenom ? `${user.prenom} ${user.nom || ''}`.trim() : user.email}</span>
          <button className="bi-logout" onClick={onLogout}>Se déconnecter</button>
        </div>
      </header>
      <div className="bi-layout">
        <Sidebar admin={user.role === 'admin'} />
        <Outlet />
        <MenuBloc exportExcel={!!user.export_excel} />
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  // Étape 2 de la connexion : { tempToken, configurer } entre Microsoft et la session.
  const [etape2, setEtape2] = useState(null);
  // Garde-fou contre le double-échange du code OIDC en StrictMode (même patron que le CRM).
  const oidcHandled = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/connecteur' && params.get('demande')) {
      try { sessionStorage.setItem(DEMANDE_MCP, params.get('demande')); } catch (e) { /* navigation privée */ }
    }

    // DEV UNIQUEMENT : ?jeton=<jwt> dans l'URL pose le jeton et recharge —
    // évite la manipulation console pour prévisualiser sans Entra.
    // process.env.NODE_ENV vaut 'production' dans le build : ce chemin
    // n'existe pas dans l'application déployée.
    if (process.env.NODE_ENV === 'development' && params.get('jeton')) {
      localStorage.setItem('bi-token', params.get('jeton'));
      window.location.replace('/');
      return;
    }

    const isOidcReturn = window.location.pathname === '/oidc/callback' || params.has('code');

    if (isOidcReturn && !oidcHandled.current) {
      oidcHandled.current = true;
      handleOidcCallback(params);
    } else {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    if (localStorage.getItem('bi-token')) {
      try {
        const res = await auth.getMe();
        setUser(res.data.user);
      } catch (e) {
        localStorage.removeItem('bi-token');
      }
    }
    setLoading(false);
  };

  const handleOidcCallback = async (params) => {
    const clearUrl = () => window.history.replaceState({}, '', '/');
    try {
      const oauthError = params.get('error_description') || params.get('error');
      if (oauthError) throw new Error(oauthError);
      const code = params.get('code');
      const state = params.get('state');
      if (!code || !state) throw new Error('Réponse Microsoft incomplète');
      const res = await auth.microsoftCallback(code, state);
      clearUrl();
      if (res.data.temp_token) {
        localStorage.removeItem('bi-token');
        setEtape2({ tempToken: res.data.temp_token, configurer: !!res.data.requires_2fa_setup });
      } else {
        localStorage.setItem('bi-token', res.data.token);
        setUser(res.data.user);
      }
    } catch (error) {
      clearUrl();
      setAuthError(error.response?.data?.error || error.message || 'Échec de la connexion Microsoft');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('bi-token');
    setUser(null);
  };

  if (loading) return null;
  if (!user && etape2) {
    return <DoubleAuth tempToken={etape2.tempToken} configurer={etape2.configurer}
      onSuccess={(u) => { setEtape2(null); setUser(u); }} onAnnuler={() => setEtape2(null)} />;
  }
  if (!user) return <Login authError={authError} />;
  if (lireDemande()) {
    return <Connecteur demande={lireDemande()} onFin={() => { try { sessionStorage.removeItem(DEMANDE_MCP); } catch (e) { /* rien */ } }} />;
  }

  return (
    <BrowserRouter>
      <BiProvider user={user}>
        <Routes>
          <Route element={<Layout user={user} onLogout={handleLogout} />}>
            <Route index element={<Shell />} />
            {/* Rapport financier transverse : gabarit dédié (balance âgée), pas
                le gabarit série-mensuelle générique — route statique prioritaire. */}
            <Route path="r/creances-clients" element={<Creances />} />
            <Route path="r/or-en-cours" element={<OrEnCours />} />
            <Route path="r/am-or-en-cours" element={<OrEnCours branche="auto-metropole" />} />
            <Route path="r/am-pr-jour" element={<PrJour branche="auto-metropole" />} />
            <Route path="r/am-pr-performance" element={<PrPerformance branche="auto-metropole" />} />
            <Route path="r/am-pr-analyse" element={<PrAnalyse branche="auto-metropole" />} />
            <Route path="r/am-pr-stock" element={<PrStock branche="auto-metropole" />} />
            <Route path="r/am-pr-stock-evolution" element={<PrStockEvolution branche="auto-metropole" />} />
            <Route path="r/am-creances-clients" element={<Creances branche="auto-metropole" />} />
            <Route path="r/am-tresorerie" element={<Tresorerie branche="auto-metropole" />} />
            <Route path="r/vn-stock" element={<Stock type="vn" />} />
            <Route path="r/vn-stock-evolution" element={<StockEvolution type="vn" />} />
            <Route path="r/vo-stock-evolution" element={<StockEvolution type="vo" />} />
            <Route path="r/vo-stock" element={<Stock type="vo" />} />
            <Route path="r/am-vn-stock" element={<Stock type="vn" branche="auto-metropole" />} />
            <Route path="r/am-vo-stock" element={<Stock type="vo" branche="auto-metropole" />} />
            <Route path="r/tresorerie" element={<Tresorerie />} />
            <Route path="r/pl" element={<Pl />} />
            <Route path="r/sig" element={<Sig />} />
            <Route path="r/am-sig" element={<Sig branche="auto-metropole" />} />
            {/* Groupe (2026-09-16) : mêmes écrans finance, périmètre consolidé
                des 27 sociétés (dossier /GROUPE du dépôt). */}
            <Route path="r/gr-creances-clients" element={<Creances branche="groupe" />} />
            <Route path="r/gr-tresorerie" element={<Tresorerie branche="groupe" />} />
            <Route path="r/fournisseurs" element={<Fournisseurs />} />
            <Route path="r/fournisseurs-analyse" element={<FournisseursAnalyse />} />
            {/* Rapports VN riches (gabarits communs, réutilisés demain pour VO). */}
            <Route path="r/vn-performance" element={<VentesPerformance type="vn" />} />
            <Route path="r/vn-jour" element={<VentesJour type="vn" />} />
            <Route path="r/vo-jour" element={<VentesJour type="vo" />} />
            <Route path="r/vn-analyse" element={<VentesAnalyse type="vn" />} />
            <Route path="r/vo-performance" element={<VentesPerformance type="vo" />} />
            <Route path="r/vo-analyse" element={<VentesAnalyse type="vo" />} />
            {/* Auto Métropole (groupe Sellens) : mêmes gabarits, mart métropole.
                Pas de Financement & Roole (absents de l'export Sellens). */}
            <Route path="r/am-vn-performance" element={<VentesPerformance type="vn" branche="auto-metropole" />} />
            <Route path="r/am-vn-jour" element={<VentesJour type="vn" branche="auto-metropole" />} />
            <Route path="r/am-vo-jour" element={<VentesJour type="vo" branche="auto-metropole" />} />
            <Route path="r/am-vn-analyse" element={<VentesAnalyse type="vn" branche="auto-metropole" />} />
            <Route path="r/am-vo-performance" element={<VentesPerformance type="vo" branche="auto-metropole" />} />
            <Route path="r/am-vo-analyse" element={<VentesAnalyse type="vo" branche="auto-metropole" />} />
            <Route path="r/marche" element={<Marche />} />
            <Route path="r/marche-analyse" element={<MarcheAnalyse />} />
            <Route path="r/apv-performance" element={<ApvPerformance />} />
            <Route path="r/apv-productivite" element={<ApvProductivite />} />
            <Route path="r/apv-ventes-additionnelles" element={<ApvVentesAdd />} />
            <Route path="r/apv-jour" element={<ApvJour />} />
            <Route path="r/apv-analyse" element={<ApvAnalyse />} />
            <Route path="r/pr-jour" element={<PrJour />} />
            <Route path="r/pr-performance" element={<PrPerformance />} />
            <Route path="r/pr-analyse" element={<PrAnalyse />} />
            <Route path="r/pr-stock" element={<PrStock />} />
            <Route path="r/pr-stock-evolution" element={<PrStockEvolution />} />
            <Route path="r/am-apv-performance" element={<ApvPerformance branche="auto-metropole" />} />
            <Route path="r/am-apv-jour" element={<ApvJour branche="auto-metropole" />} />
            <Route path="r/am-apv-analyse" element={<ApvAnalyse branche="auto-metropole" />} />
            <Route path="r/am-apv-facturation-type" element={<FacturationType />} />
            <Route path="r/rh-masse-salariale" element={<RhMasseSalariale />} />
            <Route path="r/rh-analyse" element={<RhAnalyse />} />
            {/* Administration : la cible se règle AILLEURS que sur l'écran qui
                mesure son atteinte. Hors de /r/ : ce n'est pas un rapport. */}
            <Route path="admin/objectifs" element={<Objectifs />} />
            {/* Ancienne adresse (avant la page unique du 30/09/2026). */}
            <Route path="admin/objectifs-apv" element={<Navigate to="/admin/objectifs?metier=APV" replace />} />
            <Route path="admin/referentiel-marche" element={<ReferentielMarche />} />
            <Route path="admin/ventes-additionnelles" element={<ReferentielVentesAdd />} />
            <Route path="admin/acces" element={<AdminAcces />} />
            <Route path="admin/societes" element={<AdminSocietes />} />
            <Route path="r/:slug" element={<Rapport />} />
            <Route path="*" element={<Shell />} />
          </Route>
        </Routes>
      </BiProvider>
    </BrowserRouter>
  );
}

export default App;
